/**
 * A decisão do perito sobre UMA CCB, na mesa eletrônica (E74.F3, ADR-0075 §3).
 *
 * O perito não compara traço: ele lê o que foi apurado e decide. O formulário
 * tem três partes e uma regra:
 *
 * 1. **Grau** — nasce com o apurado selecionado. Mudá-lo revela o campo de
 *    justificativa e **exige** preenchê-lo. A régua é do servidor (que recusa
 *    com `justificativa_requerida`); aqui ela só evita o clique inútil.
 * 2. **Conclusão** — nasce com a redação padrão do grau selecionado, já
 *    preenchida com os dados desta CCB. Trocar o grau troca o texto **apenas
 *    enquanto o perito não o editou**: sobrescrever um texto escrito à mão
 *    porque ele mexeu num seletor seria perder trabalho sem aviso.
 * 3. **Observações** — o bloco livre que substitui os cinco formulários de
 *    veredito do PRD 02 (ADR-0075, "o que fica de fora").
 *
 * Concluído, quem apresenta é `ConclusaoRegistrada` (leitura + reabrir).
 *
 * E78.F4 — a decisão passou a morar numa coluna lateral, ao lado do quadro, e
 * ganhou duas coisas que a mesa antiga escondia: o grau apurado e a tendência
 * no CABEÇALHO (antes perdidos no meio do quadro, que o perito precisava rolar
 * para reencontrar) e o "Próxima peça", que tira o custo de voltar à tabela a
 * cada conclusão. Cada conclusão continua um ato do perito — não há ação em
 * lote (decisão do owner, 2026-09-01).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, Field, Select, Textarea } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  ConclusaoEletronicaInput,
  ExameEletronicoCcbData,
  GrauConvergencia,
} from '@/lib/exame_eletronico';
import type { Tendencia } from '@/lib/pre_analise';

import { ConclusaoRegistrada } from './ConclusaoRegistrada';
import { GrauETendencia } from './QuadroVerificacaoTabelas';

const t = ptBR.mesaEletronica;
const tq = ptBR.exameEletronico.quadro;

const GRAUS: readonly GrauConvergencia[] = [
  'forte',
  'medio',
  'fraco',
  'inconclusivo',
  'inconsistente',
];

export type DecisaoAcao = 'concluindo' | 'reabrindo' | null;

export interface DecisaoDoPeritoProps {
  ccb: ExameEletronicoCcbData;
  idPrefix: string;
  acaoEmCurso: DecisaoAcao;
  /** Título da próxima peça a decidir; `null` quando não há outra. */
  proximaPeca?: string | null;
  onConcluir: (conclusao: ConclusaoEletronicaInput) => void;
  onReabrir: (motivo: string) => void;
  onProximaPeca?: () => void;
}

/** O que o motor apurou nesta peça — a régua que a decisão parte de. */
function CabecalhoDoApurado({
  grau,
  tendencia,
}: {
  grau: GrauConvergencia;
  tendencia: Tendencia | null;
}): JSX.Element {
  return (
    <div data-testid="decisao-apurado">
      <h4 className="font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
        {t.apuradoCabecalho}
      </h4>
      <div className="mt-sc-2">
        <GrauETendencia grau={grau} tendencia={tendencia} />
      </div>
    </div>
  );
}

/**
 * O passo seguinte, sem voltar à tabela. Sem outra peça a decidir, o botão sai
 * e a frase toma o lugar — nunca um botão que não leva a lugar nenhum.
 */
function ProximaPeca({
  proximaPeca,
  bloqueado,
  onProximaPeca,
}: {
  proximaPeca: string | null;
  bloqueado: boolean;
  onProximaPeca: (() => void) | undefined;
}): JSX.Element {
  if (proximaPeca === null || onProximaPeca === undefined) {
    return (
      <p className="mt-sc-4 text-caption text-fg-dim" data-testid="decisao-sem-proxima">
        {t.semProximaPeca}
      </p>
    );
  }
  return (
    <Button
      type="button"
      variant="secondary"
      className="mt-sc-4"
      disabled={bloqueado}
      onClick={onProximaPeca}
      data-testid="decisao-proxima-peca"
    >
      {`${t.proximaPeca}: ${proximaPeca}`}
    </Button>
  );
}

/** O estado editável do formulário e as transições que o mantêm coerente. */
function useRascunhoDaDecisao(ccb: ExameEletronicoCcbData) {
  const grauApurado = ccb.exame?.resultado?.grau_convergencia ?? null;
  const status = ccb.exame?.status;
  const sugeridas = useMemo(() => ccb.conclusoes_sugeridas ?? {}, [ccb.conclusoes_sugeridas]);

  const [grau, setGrau] = useState<GrauConvergencia | null>(grauApurado);
  const [justificativa, setJustificativa] = useState('');
  const [conclusao, setConclusao] = useState('');
  const [observacoes, setObservacoes] = useState('');
  // Enquanto falso, trocar o grau troca a conclusão; depois da primeira edição
  // manual o texto é do perito e o seletor não o toca mais.
  const [editada, setEditada] = useState(false);

  // O exame chega (ou volta a chegar, depois de uma reabertura) e o rascunho se
  // realinha ao que o servidor diz. Sem isto, reabrir deixaria a tela com a
  // decisão antiga digitada.
  useEffect(() => {
    setGrau(grauApurado);
    setJustificativa('');
    setConclusao(grauApurado === null ? '' : (sugeridas[grauApurado] ?? ''));
    setObservacoes('');
    setEditada(false);
  }, [grauApurado, sugeridas, status]);

  const trocarGrau = useCallback(
    (novo: GrauConvergencia) => {
      setGrau(novo);
      if (!editada) setConclusao(sugeridas[novo] ?? '');
    },
    [editada, sugeridas],
  );

  const restaurar = useCallback(() => {
    if (grau === null) return;
    setConclusao(sugeridas[grau] ?? '');
    setEditada(false);
  }, [grau, sugeridas]);

  const editarConclusao = useCallback((texto: string) => {
    setConclusao(texto);
    setEditada(true);
  }, []);

  return {
    grauApurado,
    grau,
    justificativa,
    conclusao,
    observacoes,
    editada,
    trocarGrau,
    restaurar,
    editarConclusao,
    setJustificativa,
    setObservacoes,
  };
}

export function DecisaoDoPerito({
  ccb,
  idPrefix,
  acaoEmCurso,
  proximaPeca = null,
  onConcluir,
  onReabrir,
  onProximaPeca,
}: DecisaoDoPeritoProps): JSX.Element | null {
  const rascunho = useRascunhoDaDecisao(ccb);
  const [erro, setErro] = useState<string | null>(null);

  const exame = ccb.exame;
  const emCurso = acaoEmCurso !== null;

  const submeter = useCallback((): void => {
    const { grau, grauApurado, conclusao, justificativa, observacoes } = rascunho;
    if (grau === null) return;
    if (conclusao.trim() === '') {
      setErro(t.conclusaoObrigatoria);
      return;
    }
    const reclassificado = grau !== grauApurado;
    if (reclassificado && justificativa.trim() === '') {
      setErro(t.justificativaObrigatoria);
      return;
    }
    setErro(null);
    onConcluir({
      grau,
      grau_justificativa: reclassificado ? justificativa.trim() : null,
      conclusao_texto: conclusao.trim(),
      observacoes_perito: observacoes.trim() === '' ? null : observacoes.trim(),
    });
  }, [onConcluir, rascunho]);

  if (exame == null || exame.resultado == null) return null;

  const apurado = (
    <CabecalhoDoApurado
      grau={exame.resultado.grau_convergencia}
      tendencia={exame.tendencia ?? null}
    />
  );
  const passoSeguinte = (
    <ProximaPeca proximaPeca={proximaPeca} bloqueado={emCurso} onProximaPeca={onProximaPeca} />
  );

  if (exame.status === 'concluido') {
    return (
      <>
        {apurado}
        <ConclusaoRegistrada
          exame={exame}
          idPrefix={idPrefix}
          reabrindoEmCurso={acaoEmCurso === 'reabrindo'}
          bloqueado={emCurso}
          onReabrir={onReabrir}
        />
        {passoSeguinte}
      </>
    );
  }

  const reclassificado = rascunho.grau !== rascunho.grauApurado;

  return (
    <>
      {apurado}
      <section className="mt-sc-6" data-testid={`${idPrefix}-decisao`} data-concluido="false">
        <h4 className="font-display text-[13px] uppercase tracking-[0.06em] text-fg-muted">
          {t.decisaoTitulo}
        </h4>

        <Field
          label={t.grauLabel}
          htmlFor={`${idPrefix}-grau`}
          description={t.grauAjuda}
          className="mt-sc-3"
        >
          <Select
            id={`${idPrefix}-grau`}
            value={rascunho.grau ?? ''}
            disabled={emCurso}
            onChange={(event) => rascunho.trocarGrau(event.target.value as GrauConvergencia)}
          >
            {GRAUS.map((opcao) => (
              <option key={opcao} value={opcao}>
                {tq.grauOpcoes[opcao]}
              </option>
            ))}
          </Select>
        </Field>

        {reclassificado ? (
          <Field
            label={t.justificativaLabel}
            htmlFor={`${idPrefix}-justificativa`}
            className="mt-sc-3"
          >
            <Textarea
              id={`${idPrefix}-justificativa`}
              rows={2}
              value={rascunho.justificativa}
              placeholder={t.justificativaPlaceholder}
              disabled={emCurso}
              onChange={(event) => rascunho.setJustificativa(event.target.value)}
            />
          </Field>
        ) : null}

        <Field
          label={t.conclusaoLabel}
          htmlFor={`${idPrefix}-conclusao`}
          description={t.conclusaoAjuda}
          className="mt-sc-3"
        >
          <Textarea
            id={`${idPrefix}-conclusao`}
            rows={8}
            value={rascunho.conclusao}
            disabled={emCurso}
            onChange={(event) => rascunho.editarConclusao(event.target.value)}
          />
        </Field>
        {rascunho.editada ? (
          <Button
            type="button"
            variant="ghost"
            onClick={rascunho.restaurar}
            disabled={emCurso}
            data-testid={`${idPrefix}-restaurar`}
          >
            {t.conclusaoRestaurar}
          </Button>
        ) : null}

        <Field label={t.observacoesLabel} htmlFor={`${idPrefix}-observacoes`} className="mt-sc-3">
          <Textarea
            id={`${idPrefix}-observacoes`}
            rows={3}
            value={rascunho.observacoes}
            placeholder={t.observacoesPlaceholder}
            disabled={emCurso}
            onChange={(event) => rascunho.setObservacoes(event.target.value)}
          />
        </Field>

        {erro !== null ? (
          <p
            className="mt-sc-3 text-body-sm text-danger-700"
            role="alert"
            data-testid={`${idPrefix}-erro-local`}
          >
            {erro}
          </p>
        ) : null}

        <Button
          type="button"
          className="mt-sc-4"
          onClick={submeter}
          disabled={emCurso}
          data-testid={`${idPrefix}-concluir`}
        >
          {acaoEmCurso === 'concluindo' ? t.concluindo : t.concluir}
        </Button>
      </section>
      {passoSeguinte}
    </>
  );
}
